/**
 * FloatingCommandMenu — reusable Blender-style centered command popup.
 *
 * Provides a glassmorphic panel with:
 * - Uppercase section title
 * - Optional navigable option list (ArrowUp/Down + Enter to select)
 * - Escape to close, key-blocking for non-modifier keys
 * - Outside-click dismiss (via Popover)
 *
 * Positioning is delegated to Popover: centered by default, or anchored
 * near a viewport point when `anchorPoint` is provided.
 *
 * Used by DeleteMenu, MergeMenu, RelaxMenu, and any future
 * centered-popup menus that follow the same Blender convention.
 */

import type { ReactNode, PointerEvent as ReactPointerEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "../app/store.ts";
import { Popover } from "./Popover.tsx";
import { getSelectableRowStyle } from "./selectableRow.ts";
import { renderLabelWithShortcut } from "./shared.tsx";
import {
  COLOR_TEXT_FAINT,
  FONT_SIZE_SECTION,
  FONT_SIZE_SMALL,
  RADIUS_CONTROL,
} from "./tokens.ts";

/** Distance in pixels before a pointerdown inside the menu is treated as
 *  a drag. Below this threshold the pointerdown is still a normal click,
 *  so tapping an option list item dispatches its onClick as before. */
const DRAG_THRESHOLD = 5;

/** CSS selector for interactive controls that should never start a drag
 *  when clicked. Matches all form controls, slider handles, and the
 *  `[data-no-drag]` escape hatch for custom components. */
const NON_DRAG_TARGETS =
  "button, input, textarea, select, [role='button'], [role='slider'], [data-no-drag]";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CommandMenuOption {
  id: string;
  label: string;
  /** When true, this entry renders as a visual separator line instead of a clickable option. */
  separator?: boolean;
  /** When true, this entry renders as a section header label instead of a clickable option. */
  sectionHeader?: boolean;
  /** Single lowercase letter that triggers this option when pressed while the menu is open. The matching character in label is underlined. */
  shortcutKey?: string;
}

interface FloatingCommandMenuProps {
  open: boolean;
  onClose: () => void;
  title: string;
  width?: number;
  /**
   * Optional viewport-coordinate anchor point.  When provided the menu is
   * positioned near this point instead of viewport-centred.
   */
  anchorPoint?: { x: number; y: number } | null;
  /**
   * Navigable options rendered as a list. When present, ArrowUp/Down
   * and Enter-to-select keyboard navigation is enabled.
   */
  options?: CommandMenuOption[];
  /** Called when a list option is selected (click or Enter). */
  onSelect?: (id: string) => void;
  /**
   * Unique key for menu-recall positioning.  When set, the menu remembers the
   * last-selected option and, on re-open, offsets itself so that item lands
   * directly under the cursor (Blender-style behaviour).
   */
  panelKey?: string;
  /**
   * Custom content rendered instead of (or below) the option list.
   * Use this for slider controls, status text, or any non-list UI.
   */
  children?: ReactNode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FloatingCommandMenu({
  open,
  onClose,
  title,
  width = 180,
  anchorPoint,
  options,
  onSelect,
  panelKey,
  children,
}: FloatingCommandMenuProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const lastSelectedId = useStore((s) =>
    panelKey ? s.panelLastSelected[panelKey] : undefined,
  );
  const setPanelLastSelected = useStore((s) => s.setPanelLastSelected);

  // ------------------------------------------------------------------
  // Drag-to-reposition state
  // ------------------------------------------------------------------

  // Explicit position override passed into Popover whenever the user has
  // dragged the menu. `null` means "use the default anchor/centered
  // placement Popover picks itself."
  const [dragPosition, setDragPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const popoverRootRef = useRef<HTMLDivElement | null>(null);
  // Active drag session state held in a ref so pointer handlers don't
  // re-render the component during a drag.
  const dragSessionRef = useRef<{
    startX: number;
    startY: number;
    baseTop: number;
    baseLeft: number;
    active: boolean;
  } | null>(null);

  // Reset drag position when the menu closes so the next open starts at
  // the natural anchor/centered placement.
  useEffect(() => {
    if (!open) {
      setDragPosition(null);
      dragSessionRef.current = null;
    }
  }, [open]);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    // Skip drag entirely when the pointerdown lands on an interactive
    // control — buttons, inputs, slider handles, or anything explicitly
    // marked [data-no-drag]. Those elements handle their own pointer
    // events and we don't want to steal their clicks.
    const target = e.target as HTMLElement;
    if (target.closest(NON_DRAG_TARGETS)) return;

    const root = popoverRootRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();

    dragSessionRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseTop: rect.top,
      baseLeft: rect.left,
      active: false,
    };

    const onMove = (ev: PointerEvent) => {
      const session = dragSessionRef.current;
      if (!session) return;
      const dx = ev.clientX - session.startX;
      const dy = ev.clientY - session.startY;
      if (!session.active) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD)
          return;
        session.active = true;
      }
      setDragPosition({
        top: session.baseTop + dy,
        left: session.baseLeft + dx,
      });
    };

    const onUp = () => {
      const wasActive = dragSessionRef.current?.active ?? false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragSessionRef.current = null;

      // A drag ends with a native `click` event dispatched to whatever
      // the pointer happens to be over. Swallow the first click that
      // follows an actual drag so option list items / controls aren't
      // accidentally activated where the drag terminates.
      if (wasActive) {
        const swallow = (ce: Event) => {
          ce.preventDefault();
          ce.stopPropagation();
          window.removeEventListener("click", swallow, true);
        };
        window.addEventListener("click", swallow, true);
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }, []);

  // On open, pre-highlight the last-selected item (or fall back to first)
  const recallIdx =
    panelKey && lastSelectedId && options
      ? options.findIndex((o) => o.id === lastSelectedId)
      : -1;

  useEffect(() => {
    if (open) setActiveIdx(recallIdx >= 0 ? recallIdx : 0);
  }, [open, recallIdx]);

  // Anchor-offset callback: tells Popover how far to shift so the recalled
  // item lands under the cursor.  Uses data-menu-item-id attributes on the
  // rendered option elements.
  const getAnchorOffset = useCallback(
    (el: HTMLDivElement) => {
      if (!lastSelectedId || recallIdx < 0) return { x: 0, y: 0 };
      const target = el.querySelector<HTMLElement>(
        `[data-menu-item-id="${lastSelectedId}"]`,
      );
      if (!target) return { x: 0, y: 0 };
      const popRect = el.getBoundingClientRect();
      const itemRect = target.getBoundingClientRect();
      return {
        x: itemRect.left - popRect.left + itemRect.width / 2,
        y: itemRect.top - popRect.top + itemRect.height / 2,
      };
    },
    [lastSelectedId, recallIdx],
  );

  // Keyboard navigation (separator-skipping, Escape, Enter)
  const select = useCallback(
    (opt: CommandMenuOption) => {
      if (panelKey) setPanelLastSelected(panelKey, opt.id);
      onSelect?.(opt.id);
    },
    [onSelect, panelKey, setPanelLastSelected],
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      const count = options?.length ?? 0;

      // Find next non-separator index in a direction
      const nextSelectable = (from: number, dir: 1 | -1): number => {
        let idx = from + dir;
        while (
          idx >= 0 &&
          idx < count &&
          (options?.[idx]?.separator || options?.[idx]?.sectionHeader)
        )
          idx += dir;
        return idx >= 0 && idx < count ? idx : from;
      };

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
        case "ArrowDown":
          if (count > 0) {
            e.preventDefault();
            e.stopPropagation();
            setActiveIdx((prev) => nextSelectable(prev, 1));
          }
          break;
        case "ArrowUp":
          if (count > 0) {
            e.preventDefault();
            e.stopPropagation();
            setActiveIdx((prev) => nextSelectable(prev, -1));
          }
          break;
        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          if (count > 0 && options && !options[activeIdx]?.separator) {
            select(options[activeIdx]);
          } else if (!options || count === 0) {
            onClose();
          }
          break;
        default:
          if (!(e.metaKey || e.ctrlKey)) {
            const key = e.key.toLowerCase();
            const matchIdx =
              key.length === 1
                ? (options?.findIndex(
                    (o) =>
                      !o.separator && !o.sectionHeader && o.shortcutKey === key,
                  ) ?? -1)
                : -1;
            if (matchIdx >= 0) {
              e.preventDefault();
              e.stopPropagation();
              select(options![matchIdx]);
            } else {
              e.stopPropagation();
            }
          }
          break;
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [open, activeIdx, onClose, select, options]);

  // `cursor: grab` hints the whole container is draggable; the pointer
  // handler further filters out clicks on interactive controls.
  const menuStyle = {
    width,
    padding: "10px 8px",
    display: "flex" as const,
    flexDirection: "column" as const,
    gap: 2,
    cursor: "grab" as const,
  };

  // Popover handles portal, positioning, click-outside, and dismiss.
  // We use centered mode when no anchorPoint, anchor-point mode otherwise.
  // `positionOverride` wins when the user has dragged the menu; `rootRef`
  // gives us access to the rendered popover container so we can measure
  // it on drag start.
  const popoverProps = anchorPoint
    ? ({
        anchorPoint,
        open,
        onClose,
        style: menuStyle,
        getAnchorOffset,
        positionOverride: dragPosition,
        rootRef: popoverRootRef,
      } as const)
    : {
        centered: true as const,
        open,
        onClose,
        style: menuStyle,
        positionOverride: dragPosition,
        rootRef: popoverRootRef,
      };

  return (
    <Popover {...popoverProps}>
      <div onPointerDown={onPointerDown} style={{ display: "contents" }}>
        <span
          style={{
            fontSize: FONT_SIZE_SECTION,
            color: COLOR_TEXT_FAINT,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            padding: "0 6px 4px",
          }}
        >
          {title}
        </span>

        {options?.map((opt, i) => {
          if (opt.sectionHeader) {
            return (
              <span
                key={opt.id}
                style={{
                  fontSize: FONT_SIZE_SECTION,
                  color: COLOR_TEXT_FAINT,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  padding: "6px 6px 2px",
                }}
              >
                {opt.label}
              </span>
            );
          }
          if (opt.separator) {
            return (
              <div
                key={opt.id}
                style={{
                  height: 1,
                  background: COLOR_TEXT_FAINT,
                  opacity: 0.3,
                  margin: "4px 6px",
                }}
              />
            );
          }
          const isActive = i === activeIdx;
          return (
            <div
              key={opt.id}
              data-menu-item-id={opt.id}
              onClick={() => select(opt)}
              onPointerEnter={() => setActiveIdx(i)}
              style={{
                padding: "5px 8px",
                borderRadius: RADIUS_CONTROL,
                ...getSelectableRowStyle(isActive),
                fontSize: FONT_SIZE_SMALL,
                cursor: "pointer",
              }}
            >
              {renderLabelWithShortcut(opt.label, opt.shortcutKey)}
            </div>
          );
        })}

        {children}
      </div>
    </Popover>
  );
}
